import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { useLanguage } from "../i18n/LanguageContext";

const IMG = {
  interior: "https://static.wixstatic.com/media/6765ec_bb179728a77a4eec9d46f61ce032858f~mv2.jpg/v1/fill/w_1200,h_900,al_c,q_85,usm_0.66_1.00_0.01/6765ec_bb179728a77a4eec9d46f61ce032858f~mv2.jpg",
  stone: "https://images.squarespace-cdn.com/content/v1/659d10446c74f52c8b4fb736/3bf6a3a4-2912-41ab-9b22-fe2ee580efe2/6-Garden-ACA+Architects-AmberByOneIslans-min.jpg",
};

export default function About() {
  const { t, images } = useLanguage();
  return (
    <main className="pt-[72px]">
      <Seo page="about" path="/about" />

      <section className="py-20 sm:py-28" data-testid="about-hero-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold mb-6">{t.about.pageEyebrow}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#142D44] leading-[1.08]">
              <MaskedLines lines={t.about.pageTitle.split(". ").map((s, i, arr) => (i < arr.length - 1 ? s + "." : s))} />
            </h1>
            <p className="mt-7 text-[#4A5568] leading-relaxed max-w-xl text-lg">{t.about.pageIntro}</p>
          </div>
          <Reveal className="lg:col-span-4 lg:col-start-9">
            <div className="overflow-hidden rounded-2xl aspect-[4/5]">
              <img src={images.about_stone || IMG.stone} alt="Limestone texture detail" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="py-24 sm:py-28" data-testid="about-blocks-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <div className="sticky top-28 overflow-hidden rounded-2xl aspect-[3/4]">
              <img src={images.about_interior || IMG.interior} alt="Architectural interior with staircase" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7 space-y-14">
            {t.about.blocks.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05} className="border-t border-[#E3DED4] pt-8">
                <span className="font-mono text-xs text-[#B85D2A]">0{i + 1}</span>
                <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-[#142D44]">{b.title}</h2>
                <p className="mt-4 text-[#4A5568] leading-relaxed">{b.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#EFF4F8] border-y border-[#D8E2EC]" data-testid="about-values-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#B85D2A] font-semibold">{t.values.eyebrow}</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-[#142D44] leading-tight max-w-2xl">{t.values.title}</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
            {t.values.list.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <span className="font-mono text-xs text-[#B85D2A]">0{i + 1}</span>
                <h3 className="mt-3 font-serif text-xl text-[#142D44] leading-snug">{v.title}</h3>
                <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">{v.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1E3A5F] border-t border-white/10" data-testid="about-quote-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="font-serif text-2xl sm:text-3xl text-[#FBF9F5] leading-relaxed">"{t.about.quote}"</p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-[#B85D2A]">Horizon Advisors · Cyprus</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-[#F4F1EA] border-t border-[#E3DED4]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#142D44]">{t.cta.title}</h2>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="group mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-[#1E3A5F] text-[#FBF9F5] text-sm font-medium hover:bg-[#B85D2A] transition-colors duration-300"
            >
              {t.cta.btn}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
